<script lang="ts" setup>
import { accountService } from "@/app/services/AccountService";
import { orderService } from "@/app/services/OrderService";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import Header from "@/components/organisms/common/Header.vue";
import Calendar from "@/components/organisms/desired_work_schedules/Calendar.vue";
import ScheduleFormBottomSheet from "@/components/organisms/desired_work_schedules/ScheduleFormBottomSheet.vue";
import SuggestOrderModal from "@/components/organisms/desired_work_schedules/SuggestOrderModal.vue";
import router from "@/router";
import { masterStore } from "@/store/master";
import { DesiredWorkSchedule } from "@/types/desired_work_schedule/DesiredWorkSchedule";
import { SlimOrder } from "@/types/order/Order";
import {
  addMonths,
  endOfMonth,
  formatFromUnixTimestamp,
  now
} from "@/utils/date";
import { useLoader } from "@/utils/loader";
import { useErrorModal } from "@/utils/modal";
import { captureException } from "@sentry/vue";
import { computed, onMounted, ref } from "vue";

const loader = useLoader();
const errorModal = useErrorModal();

const selectedDate = ref<string | null>(null);
const isBottomSheetVisible = ref<boolean>(false);
const suggestOrderModalVisible = ref<SlimOrder | null>(null);
const selectedSchedules = ref<DesiredWorkSchedule[]>([]);
const suggestionOrders = ref<SlimOrder[]>([]);
// おすすめ案件モーダルはこのカウンターが3の倍数の時にランダムで表示する
const suggestOrderModalCounter = ref<number>(0);

onMounted(async () => {
  try {
    loader.show();
    const [schedules, orders] = await Promise.all([
      accountService.fetchDesiredWorkSchedules(),
      fetchSuggestionOrders()
    ]);
    selectedSchedules.value = schedules;
    suggestionOrders.value = orders;
  } catch (e) {
    await errorModal.showErrorModal(e as Error);
  } finally {
    loader.hide();
  }
});

const holidays = computed(() => {
  return masterStore.japaneseHolidays.map(holiday => holiday.date);
});

async function fetchSuggestionOrders() {
  const favoritedOffice = await accountService.fetchFavoriteOffices();
  const currentTime = now();
  const today = formatFromUnixTimestamp(currentTime, "yyyy-MM-dd");
  const endOfDateMonthAfterNext = formatFromUnixTimestamp(
    endOfMonth(addMonths(currentTime, 2)),
    "yyyy-MM-dd"
  );

  const orders = await orderService.searchOrdersByOfficeIds({
    office__in: favoritedOffice.results
      .map(office => office.office.id)
      .join(","),
    date__gte: today,
    date__lte: endOfDateMonthAfterNext
  });
  return orders;
}

const dayClick = (calendarDay: string) => {
  selectedDate.value = calendarDay;
  isBottomSheetVisible.value = true;
};

const closeBottomSheet = () => {
  selectedDate.value = null;
  isBottomSheetVisible.value = false;
};

function onClickBack() {
  router.back();
}

function showSuggestOrderModal(selectedDate: string) {
  if (suggestOrderModalCounter.value % 3 !== 0) {
    suggestOrderModalCounter.value++;
    return;
  }

  if (Math.random() > 0.33) {
    return;
  }

  // 該当日付の案件を取得
  const orders = suggestionOrders.value.filter(
    order => order.date === selectedDate
  );
  if (orders.length > 0) {
    suggestOrderModalVisible.value = orders[0];
  }
  suggestOrderModalCounter.value++;
}

const onClickSave = async (schedule: {
  date: string;
  isAllDay: boolean;
  startAt: string | null;
  finishAt: string | null;
}) => {
  try {
    loader.show();
    const schedules = await accountService.updateDesiredWorkSchedule(schedule);
    selectedSchedules.value = schedules;
  } catch (e) {
    await errorModal.showErrorModal(e as Error);
  } finally {
    loader.hide();
  }

  closeBottomSheet();

  try {
    await showSuggestOrderModal(schedule.date);
  } catch (e) {
    await captureException(e);
  }
};

const onClickDelete = async (date: string) => {
  try {
    loader.show();
    const schedules = await accountService.deleteDesiredWorkSchedule(date);
    selectedSchedules.value = schedules;
  } catch (e) {
    await errorModal.showErrorModal(e as Error);
  } finally {
    loader.hide();
  }

  closeBottomSheet();
};

const onCloseSuggestOrderModal = () => {
  suggestOrderModalVisible.value = null;
};
</script>

<template>
  <div>
    <Header>
      <template v-slot:left>
        <FeatherIcon icon="chevron-left" @click="onClickBack" />
      </template>
      働きたい日
    </Header>

    <div class="container">
      <Calendar
        :desiredWorkSchedules="selectedSchedules"
        :holidays="holidays"
        @dayClick="dayClick"
      ></Calendar>

      <div class="schedule-table-wrapper"></div>
      <p>
        日付をタップして働きたい日を登録すると、オファーが届きやすくなります
      </p>
      <p class="note">
        ※ お気に入りしている事業所のみに公開されます
      </p>
    </div>

    <ScheduleFormBottomSheet
      v-model:visible="isBottomSheetVisible"
      :selectedDate="selectedDate"
      :selectedSchedules="selectedSchedules"
      @save="onClickSave"
      @delete="onClickDelete"
    />

    <SuggestOrderModal
      :order="suggestOrderModalVisible"
      @close="onCloseSuggestOrderModal"
    />
  </div>
</template>

<style lang="scss" scoped>
.container {
  padding: 16px;
}

.description {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;

  img {
    margin-left: 4px;
  }
}

.note {
  font-size: 14px;
  color: #999;
}

.schedule-table-wrapper {
  margin-bottom: 12px;
}

// 以下はカレンダーのスタイル
:deep(.vc-header) {
  height: auto;
  padding: 12px 10px;
  margin-top: 0;
}

:deep(.vc-header .vc-title) {
  pointer-events: none;
}

:deep(.vc-header .vc-arrow) {
  width: 48px;
  color: #3f88ae;
}

:deep(.vc-header.is-lg) {
  font-size: 16px;
}

:deep(.vc-bordered) {
  border: 1px solid #e5e5e5;
}

:deep(.vc-weeks) {
  padding: 0;
}

:deep(.vc-weekdays) {
  border-top: 1px solid #e5e5e5;
}

:deep(.vc-weekday) {
  padding-top: 16px;
  padding-bottom: 16px;
  font-size: 16px;
  font-weight: normal;
  color: #31373f;
}

:deep(.vc-week) {
  border-top: 1px solid #e5e5e5;
}

:deep(.vc-day .today) {
  font-weight: bold;
}

:deep(.vc-day .holiday) {
  color: #c2493e;
}

:deep(.vc-day-content) {
  width: 48px;
  height: 56px;
  font-size: 16px;
  font-weight: normal;
  line-height: 48px;
}

:deep(.vc-day-content:hover) {
  background-color: initial;
}

:deep(.vc-focus:focus-within) {
  box-shadow: none;
}

// 日曜日
:deep(.vc-weekday-1),
:deep(.weekday-1),
:deep(.weekday-1 .vc-highlight-content-solid),
:deep(.weekday-1 .vc-disabled) {
  color: #c2493e;
}

// 土曜日
:deep(.vc-weekday-7),
:deep(.weekday-7),
:deep(.weekday-7 .vc-highlight-content-solid),
:deep(.weekday-7 .vc-disabled) {
  color: #3f88ae;
}

:deep(.vc-highlight) {
  width: 32px;
  height: 32px;
}

:deep(.vc-highlight-bg-solid) {
  background-color: #fbce5a;
}

:deep(.vc-highlight-content-solid) {
  font-weight: normal;
  color: #31373f;
}
</style>
